import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ArrowLeft, Shield, FileText } from 'lucide-react';

export const TermsPage = () => {
  return (
    <div className="min-h-screen bg-ba-black">
      {/* Header */}
      <header className="border-b border-ba-border">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <Link to="/" className="flex items-center gap-3">
              <div className="w-10 h-10 bg-red-600 rounded-sm flex items-center justify-center">
                <span className="font-heading text-white text-lg">BA</span>
              </div>
              <span className="font-heading text-white text-xl tracking-wide">BLACK ARROW MMA</span>
            </Link>
            <Link to="/" className="text-zinc-400 hover:text-white text-sm flex items-center gap-2">
              <ArrowLeft className="w-4 h-4" />
              Back to Home
            </Link>
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
        >
          <div className="flex items-center gap-3 mb-8">
            <div className="w-12 h-12 bg-red-600/20 border border-red-600/50 rounded-sm flex items-center justify-center">
              <FileText className="w-6 h-6 text-red-500" />
            </div>
            <div>
              <h1 className="font-heading text-4xl text-white tracking-tight">TERMS OF SERVICE</h1>
              <p className="text-zinc-500 font-body text-sm">Last updated: April 2026</p>
            </div>
          </div>

          <div className="prose prose-invert max-w-none">
            <div className="bg-ba-surface border border-ba-border rounded-sm p-8 space-y-8">
              <section>
                <h2 className="font-heading text-2xl text-white mb-4">1. ACCEPTANCE OF TERMS</h2>
                <p className="text-zinc-400 font-body leading-relaxed">
                  By accessing and using Black Arrow MMA Fighter Development System ("Service"), you accept and agree to be bound by the terms and provisions of this agreement. If you do not agree to abide by these terms, please do not use this Service.
                </p>
              </section>

              <section>
                <h2 className="font-heading text-2xl text-white mb-4">2. DESCRIPTION OF SERVICE</h2>
                <p className="text-zinc-400 font-body leading-relaxed">
                  Black Arrow MMA provides a multi-tenant SaaS platform for MMA gyms to manage fighter profiles, training camps, assessments, fight schedules, attendance tracking, and related services. The Service is available to registered gyms on a subscription basis.
                </p>
              </section>

              <section>
                <h2 className="font-heading text-2xl text-white mb-4">3. USER ACCOUNTS</h2>
                <div className="text-zinc-400 font-body leading-relaxed space-y-3">
                  <p>To use the Service, you must:</p>
                  <ul className="list-disc pl-6 space-y-2">
                    <li>Register for an account with accurate information</li>
                    <li>Maintain the security of your password</li>
                    <li>Accept responsibility for all activities under your account</li>
                    <li>Notify us immediately of any unauthorized access</li>
                  </ul>
                </div>
              </section>

              <section>
                <h2 className="font-heading text-2xl text-white mb-4">4. SUBSCRIPTION AND PAYMENT</h2>
                <div className="text-zinc-400 font-body leading-relaxed space-y-3">
                  <p>Subscription details:</p>
                  <ul className="list-disc pl-6 space-y-2">
                    <li>Silver Plan: $299.99/month with core features</li>
                    <li>Gold Plan: $399.99/month with video uploads and premium features</li>
                    <li>30-day free trial available for new accounts</li>
                    <li>Billing occurs monthly on the anniversary of your subscription date</li>
                    <li>Cancellations take effect at the end of the current billing period</li>
                  </ul>
                </div>
              </section>

              <section>
                <h2 className="font-heading text-2xl text-white mb-4">5. DATA OWNERSHIP</h2>
                <p className="text-zinc-400 font-body leading-relaxed">
                  You retain all rights to the data you enter into the Service. Black Arrow MMA will not share your gym's data with third parties except as required by law or with your explicit consent. You may export your data at any time using the provided export features.
                </p>
              </section>

              <section>
                <h2 className="font-heading text-2xl text-white mb-4">6. ACCEPTABLE USE</h2>
                <div className="text-zinc-400 font-body leading-relaxed space-y-3">
                  <p>You agree not to:</p>
                  <ul className="list-disc pl-6 space-y-2">
                    <li>Use the Service for any unlawful purpose</li>
                    <li>Share your account credentials with unauthorized users</li>
                    <li>Attempt to breach or circumvent security measures</li>
                    <li>Upload malicious content or files</li>
                    <li>Resell or redistribute the Service without authorization</li>
                  </ul>
                </div>
              </section>

              <section>
                <h2 className="font-heading text-2xl text-white mb-4">7. SERVICE AVAILABILITY</h2>
                <p className="text-zinc-400 font-body leading-relaxed">
                  We strive to maintain 99.9% uptime but do not guarantee uninterrupted access. We may perform maintenance with reasonable notice. We are not liable for any damages resulting from service interruptions.
                </p>
              </section>

              <section>
                <h2 className="font-heading text-2xl text-white mb-4">8. LIMITATION OF LIABILITY</h2>
                <p className="text-zinc-400 font-body leading-relaxed">
                  Black Arrow MMA shall not be liable for any indirect, incidental, special, consequential, or punitive damages resulting from your use of the Service. Our total liability shall not exceed the amount you paid for the Service in the twelve months preceding the claim.
                </p>
              </section>

              <section>
                <h2 className="font-heading text-2xl text-white mb-4">9. TERMINATION</h2>
                <p className="text-zinc-400 font-body leading-relaxed">
                  We may terminate or suspend your account at any time for violation of these terms. You may terminate your account at any time through the Settings page. Upon termination, your data will be retained for 30 days before permanent deletion.
                </p>
              </section>

              <section>
                <h2 className="font-heading text-2xl text-white mb-4">10. CHANGES TO TERMS</h2>
                <p className="text-zinc-400 font-body leading-relaxed">
                  We reserve the right to modify these terms at any time. We will notify users of significant changes via email or in-app notification. Continued use of the Service after changes constitutes acceptance of the new terms.
                </p>
              </section>

              <section>
                <h2 className="font-heading text-2xl text-white mb-4">11. CONTACT</h2>
                <p className="text-zinc-400 font-body leading-relaxed">
                  For questions about these Terms of Service, please contact us at legal@blackarrowmma.com
                </p>
              </section>
            </div>
          </div>

          <div className="mt-8 text-center">
            <Link to="/privacy" className="text-red-500 hover:text-red-400 font-body">
              View Privacy Policy →
            </Link>
          </div>
        </motion.div>
      </main>
    </div>
  );
};

export const PrivacyPage = () => {
  return (
    <div className="min-h-screen bg-ba-black">
      {/* Header */}
      <header className="border-b border-ba-border">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <Link to="/" className="flex items-center gap-3">
              <div className="w-10 h-10 bg-red-600 rounded-sm flex items-center justify-center">
                <span className="font-heading text-white text-lg">BA</span>
              </div>
              <span className="font-heading text-white text-xl tracking-wide">BLACK ARROW MMA</span>
            </Link>
            <Link to="/" className="text-zinc-400 hover:text-white text-sm flex items-center gap-2">
              <ArrowLeft className="w-4 h-4" />
              Back to Home
            </Link>
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
        >
          <div className="flex items-center gap-3 mb-8">
            <div className="w-12 h-12 bg-green-600/20 border border-green-600/50 rounded-sm flex items-center justify-center">
              <Shield className="w-6 h-6 text-green-500" />
            </div>
            <div>
              <h1 className="font-heading text-4xl text-white tracking-tight">PRIVACY POLICY</h1>
              <p className="text-zinc-500 font-body text-sm">Last updated: April 2026</p>
            </div>
          </div>

          <div className="prose prose-invert max-w-none">
            <div className="bg-ba-surface border border-ba-border rounded-sm p-8 space-y-8">
              <section>
                <h2 className="font-heading text-2xl text-white mb-4">1. INFORMATION WE COLLECT</h2>
                <div className="text-zinc-400 font-body leading-relaxed space-y-3">
                  <p>We collect information you provide directly:</p>
                  <ul className="list-disc pl-6 space-y-2">
                    <li><strong>Account Information:</strong> Name, email address, gym name, password (encrypted)</li>
                    <li><strong>Fighter Data:</strong> Names, records, weight classes, and performance assessments</li>
                    <li><strong>Training Data:</strong> Camp schedules, fight bookings, attendance records</li>
                    <li><strong>Payment Information:</strong> Processed securely through Stripe (we do not store card details)</li>
                    <li><strong>Usage Data:</strong> Feature usage analytics, login times, and activity logs</li>
                  </ul>
                </div>
              </section>

              <section>
                <h2 className="font-heading text-2xl text-white mb-4">2. HOW WE USE YOUR INFORMATION</h2>
                <div className="text-zinc-400 font-body leading-relaxed space-y-3">
                  <p>We use collected information to:</p>
                  <ul className="list-disc pl-6 space-y-2">
                    <li>Provide and maintain the Service</li>
                    <li>Process your subscription payments</li>
                    <li>Send important updates about your account</li>
                    <li>Improve our Service based on usage patterns</li>
                    <li>Provide customer support</li>
                    <li>Detect and prevent fraud or abuse</li>
                  </ul>
                </div>
              </section>

              <section>
                <h2 className="font-heading text-2xl text-white mb-4">3. DATA STORAGE AND SECURITY</h2>
                <div className="text-zinc-400 font-body leading-relaxed space-y-3">
                  <p>We take data security seriously:</p>
                  <ul className="list-disc pl-6 space-y-2">
                    <li>All data is encrypted in transit (TLS/SSL)</li>
                    <li>Passwords are hashed using industry-standard bcrypt</li>
                    <li>Data is stored on secure cloud servers with regular backups</li>
                    <li>Access to production data is restricted to authorized personnel</li>
                    <li>We conduct regular security audits</li>
                  </ul>
                </div>
              </section>

              <section>
                <h2 className="font-heading text-2xl text-white mb-4">4. DATA SHARING</h2>
                <div className="text-zinc-400 font-body leading-relaxed space-y-3">
                  <p>We do NOT sell your data. We only share data with:</p>
                  <ul className="list-disc pl-6 space-y-2">
                    <li><strong>Payment Processors:</strong> Stripe for subscription billing</li>
                    <li><strong>Email Services:</strong> For transactional emails and notifications</li>
                    <li><strong>Cloud Providers:</strong> For hosting and data storage</li>
                    <li><strong>Law Enforcement:</strong> When required by law or valid legal process</li>
                  </ul>
                </div>
              </section>

              <section>
                <h2 className="font-heading text-2xl text-white mb-4">5. MULTI-TENANT DATA ISOLATION</h2>
                <p className="text-zinc-400 font-body leading-relaxed">
                  Your gym's data is logically isolated from other gyms. No gym can access another gym's fighters, assessments, or training data. Only super administrators have cross-gym access for support purposes.
                </p>
              </section>

              <section>
                <h2 className="font-heading text-2xl text-white mb-4">6. YOUR RIGHTS</h2>
                <div className="text-zinc-400 font-body leading-relaxed space-y-3">
                  <p>You have the right to:</p>
                  <ul className="list-disc pl-6 space-y-2">
                    <li><strong>Access:</strong> Request a copy of your data</li>
                    <li><strong>Export:</strong> Download your data in PDF or CSV format</li>
                    <li><strong>Correct:</strong> Update inaccurate information</li>
                    <li><strong>Delete:</strong> Request deletion of your account and data</li>
                    <li><strong>Opt-out:</strong> Unsubscribe from marketing communications</li>
                  </ul>
                </div>
              </section>

              <section>
                <h2 className="font-heading text-2xl text-white mb-4">7. COOKIES AND TRACKING</h2>
                <div className="text-zinc-400 font-body leading-relaxed space-y-3">
                  <p>We use:</p>
                  <ul className="list-disc pl-6 space-y-2">
                    <li><strong>Essential Cookies:</strong> For authentication and security</li>
                    <li><strong>Analytics:</strong> To understand feature usage (no personal tracking)</li>
                    <li>We do NOT use advertising cookies or third-party trackers</li>
                  </ul>
                </div>
              </section>

              <section>
                <h2 className="font-heading text-2xl text-white mb-4">8. DATA RETENTION</h2>
                <p className="text-zinc-400 font-body leading-relaxed">
                  We retain your data for as long as your account is active. After account deletion, we keep data for 30 days for recovery purposes, then permanently delete it. Aggregated, anonymized analytics data may be retained indefinitely.
                </p>
              </section>

              <section>
                <h2 className="font-heading text-2xl text-white mb-4">9. CHILDREN'S PRIVACY</h2>
                <p className="text-zinc-400 font-body leading-relaxed">
                  Our Service is not intended for users under 18 years of age. We do not knowingly collect personal information from children. If you believe a minor has provided us with personal information, please contact us.
                </p>
              </section>

              <section>
                <h2 className="font-heading text-2xl text-white mb-4">10. CHANGES TO THIS POLICY</h2>
                <p className="text-zinc-400 font-body leading-relaxed">
                  We may update this Privacy Policy from time to time. We will notify you of significant changes via email or in-app notification. The "Last updated" date at the top indicates when the policy was last revised.
                </p>
              </section>

              <section>
                <h2 className="font-heading text-2xl text-white mb-4">11. CONTACT US</h2>
                <p className="text-zinc-400 font-body leading-relaxed">
                  For privacy-related questions or to exercise your rights, contact us at privacy@blackarrowmma.com
                </p>
              </section>
            </div>
          </div>

          <div className="mt-8 text-center">
            <Link to="/terms" className="text-red-500 hover:text-red-400 font-body">
              View Terms of Service →
            </Link>
          </div>
        </motion.div>
      </main>
    </div>
  );
};

export default TermsPage;
